import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ComparisonPanel } from './ComparisonPanel';
import type { PanelState } from './ComparisonPanel';
import { wsClient } from '../services/websocket';
import type { Model, Message, WSServerMessage } from '../types';

export interface ComparisonViewProps {
  models: Model[];
  selectedModelIds: [string, string];
}

const createEmptyPanel = (): PanelState => ({
  messages: [],
  isStreaming: false,
  error: null,
  duration: null,
});

export const ComparisonView: React.FC<ComparisonViewProps> = ({
  models,
  selectedModelIds,
}) => {
  const [compareId, setCompareId] = useState<string | null>(null);
  const [panels, setPanels] = useState<[PanelState, PanelState]>([
    createEmptyPanel(),
    createEmptyPanel(),
  ]);
  const [inputValue, setInputValue] = useState('');

  const streamingContentRefs = useRef<[string, string]>(['', '']);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const compareIdRef = useRef<string | null>(null);
  const selectedModelIdsRef = useRef<[string, string]>(selectedModelIds);

  // Connect WebSocket on mount (ChatContainer normally does this, but it's unmounted in comparison mode)
  useEffect(() => {
    wsClient.connect();
    return () => {
      wsClient.disconnect();
    };
  }, []);

  // Keep refs in sync
  useEffect(() => {
    compareIdRef.current = compareId;
  }, [compareId]);

  useEffect(() => {
    selectedModelIdsRef.current = selectedModelIds;
  }, [selectedModelIds]);

  // Subscribe to comparison WebSocket events
  useEffect(() => {
    const handleMessage = (message: WSServerMessage) => {
      if (message.type === 'compare_token') {
        if (message.compareId !== compareIdRef.current) return;

        const panelIndex = message.modelId === selectedModelIdsRef.current[0] ? 0 : 1;
        streamingContentRefs.current[panelIndex] += message.token;

        setPanels((prev) => {
          const updated = [...prev] as [PanelState, PanelState];
          const panelMessages = updated[panelIndex].messages;
          const lastMsg = panelMessages[panelMessages.length - 1];

          if (lastMsg && lastMsg.role === 'assistant' && lastMsg.id.startsWith('streaming-')) {
            updated[panelIndex] = {
              ...updated[panelIndex],
              messages: [
                ...panelMessages.slice(0, -1),
                {
                  ...lastMsg,
                  content: streamingContentRefs.current[panelIndex],
                },
              ],
            };
          } else {
            const newMessage: Message = {
              id: `streaming-${panelIndex}-${Date.now()}`,
              role: 'assistant',
              content: streamingContentRefs.current[panelIndex],
              modelId: message.modelId,
              timestamp: new Date().toISOString(),
              tokenCount: 0,
            };
            updated[panelIndex] = {
              ...updated[panelIndex],
              isStreaming: true,
              messages: [...panelMessages, newMessage],
            };
          }

          return updated;
        });
      }

      if (message.type === 'compare_complete') {
        if (message.compareId !== compareIdRef.current) return;

        const panelIndex = message.modelId === selectedModelIdsRef.current[0] ? 0 : 1;
        // Capture final content before clearing
        const finalContent = streamingContentRefs.current[panelIndex];
        streamingContentRefs.current[panelIndex] = '';

        setPanels((prev) => {
          const updated = [...prev] as [PanelState, PanelState];
          const panelMessages = updated[panelIndex].messages;
          const lastMsg = panelMessages[panelMessages.length - 1];

          if (lastMsg && lastMsg.role === 'assistant') {
            updated[panelIndex] = {
              ...updated[panelIndex],
              isStreaming: false,
              duration: message.duration,
              messages: [
                ...panelMessages.slice(0, -1),
                {
                  ...lastMsg,
                  content: finalContent || lastMsg.content,
                  tokenCount: message.tokenCount,
                },
              ],
            };
          } else {
            updated[panelIndex] = {
              ...updated[panelIndex],
              isStreaming: false,
              duration: message.duration,
            };
          }

          return updated;
        });
      }

      if (message.type === 'compare_error') {
        if (message.compareId !== compareIdRef.current) return;

        const panelIndex = message.modelId === selectedModelIdsRef.current[0] ? 0 : 1;

        setPanels((prev) => {
          const updated = [...prev] as [PanelState, PanelState];
          // Remove incomplete streaming message if present
          const panelMessages = updated[panelIndex].messages;
          const lastMsg = panelMessages[panelMessages.length - 1];
          const cleanedMessages =
            lastMsg && lastMsg.role === 'assistant' && lastMsg.id.startsWith('streaming-')
              ? panelMessages.slice(0, -1)
              : panelMessages;

          updated[panelIndex] = {
            ...updated[panelIndex],
            isStreaming: false,
            error: message.error,
            messages: cleanedMessages,
          };
          return updated;
        });

        streamingContentRefs.current[panelIndex] = '';
      }
    };

    const unsubscribe = wsClient.onMessage(handleMessage);
    return unsubscribe;
  }, []);

  const isEitherStreaming = panels[0].isStreaming || panels[1].isStreaming;

  const handleSendMessage = useCallback(
    (content: string) => {
      if (!content.trim() || isEitherStreaming) return;
      if (!selectedModelIds[0] || !selectedModelIds[1]) return;

      const newCompareId = crypto.randomUUID();
      setCompareId(newCompareId);
      compareIdRef.current = newCompareId;

      const userMessage: Message = {
        id: `user-${Date.now()}`,
        role: 'user',
        content,
        modelId: '',
        timestamp: new Date().toISOString(),
        tokenCount: 0,
      };

      setPanels([
        { messages: [userMessage], isStreaming: true, error: null, duration: null },
        { messages: [userMessage], isStreaming: true, error: null, duration: null },
      ]);
      streamingContentRefs.current = ['', ''];

      try {
        wsClient.sendCompare(newCompareId, content, selectedModelIds);
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : 'Failed to send comparison request';
        setPanels([
          { messages: [userMessage], isStreaming: false, error: errorMsg, duration: null },
          { messages: [userMessage], isStreaming: false, error: errorMsg, duration: null },
        ]);
      }
    },
    [isEitherStreaming, selectedModelIds]
  );

  const handleCancel = useCallback(() => {
    if (compareId) {
      try {
        wsClient.cancelCompare(compareId);
      } catch (err) {
        console.error('Failed to send cancel:', err);
      }
      setPanels((prev) => [
        { ...prev[0], isStreaming: false },
        { ...prev[1], isStreaming: false },
      ]);
    }
  }, [compareId]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = inputValue.trim();
    if (!trimmed || isEitherStreaming) return;
    handleSendMessage(trimmed);
    setInputValue('');
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(e.target.value);
    const textarea = e.target;
    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`;
  };

  const modelA = models.find((m) => m.id === selectedModelIds[0]) || null;
  const modelB = models.find((m) => m.id === selectedModelIds[1]) || null;

  return (
    <div className="h-full flex flex-col">
      {/* Side-by-side panels */}
      <div className="flex-1 flex overflow-hidden min-h-0">
        <ComparisonPanel model={modelA} panel={panels[0]} />
        <div className="w-px bg-gray-300 dark:bg-gray-600 flex-shrink-0" />
        <ComparisonPanel model={modelB} panel={panels[1]} />
      </div>

      {/* Shared input area */}
      <div className="border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 flex-shrink-0">
        <div className="max-w-4xl mx-auto">
          <form onSubmit={handleSubmit} className="relative">
            <div className="flex items-end gap-2 bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-xl p-2 focus-within:border-purple-500 focus-within:ring-1 focus-within:ring-purple-500 transition-all">
              <textarea
                ref={textareaRef}
                value={inputValue}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                placeholder="Type a prompt to compare both models..."
                disabled={isEitherStreaming}
                rows={1}
                className="flex-1 bg-transparent border-none outline-none resize-none text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 px-2 py-1.5 text-sm"
                style={{ minHeight: '36px', maxHeight: '200px' }}
              />

              <div className="flex items-center gap-1 pb-0.5">
                {isEitherStreaming ? (
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="p-2 rounded-lg bg-red-500 hover:bg-red-600 text-white transition-colors"
                    title="Cancel"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                ) : (
                  <button
                    type="submit"
                    disabled={!inputValue.trim() || isEitherStreaming}
                    className="p-2 rounded-lg bg-purple-600 hover:bg-purple-700 disabled:bg-gray-300 dark:disabled:bg-gray-700 disabled:cursor-not-allowed text-white transition-colors"
                    title="Compare (Enter)"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                      />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          </form>

          <p className="mt-1.5 text-xs text-gray-400 dark:text-gray-500 text-center">
            Enter to compare, Shift+Enter for new line
          </p>
        </div>
      </div>
    </div>
  );
};
